import type { MonthView, TransactionView } from '../api.js'
import { addMoney, esc, money, money2, shortDate } from '../format.js'

/**
 * Reported purchases.
 *
 * Business purchases only. Shows this month's reports plus any older ones that
 * are still not reconciled, so nothing quietly falls off the bottom of the
 * list at a month boundary.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

const STATUS_CLASS: Record<string, string> = {
  'Needs receipt': 'st-need',
  'Receipt attached': 'st-att',
  Categorized: 'st-cat',
  Reconciled: 'st-rec',
}

export function renderPurchases(view: MonthView, canEdit: boolean): void {
  const reported = view.transactions.filter(
    (transaction) => transaction.report !== null || transaction.amazon !== null,
  )

  $('#repMeta').innerHTML = reported.length
    ? `${reported.length} · <span class="num">${money(
        addMoney(...reported.map((item) => item.amount)),
      )}</span>`
    : ''

  $('#replist').innerHTML = reported.length
    ? reported.map((transaction) => renderCard(transaction, view, canEdit)).join('')
    : `<li class="empty">Business purchases reported by whoever made them appear here, with their receipts.</li>`

  renderTrips(reported)
}

function renderCard(
  transaction: TransactionView,
  view: MonthView,
  canEdit: boolean,
): string {
  const status = transaction.report?.status ?? 'Needs receipt'
  const categoryName =
    view.categories.find((category) => category.id === transaction.categoryId)?.name ??
    'No category'

  const thumb = transaction.report?.hasReceipt
    ? `<a class="thumb" href="/api/receipts/${transaction.report.id}" target="_blank" rel="noopener">Receipt</a>`
    : '<span class="thumb" aria-hidden="true">No<br>receipt</span>'

  const details = [
    transaction.report?.buyer ? `by ${esc(transaction.report.buyer)}` : '',
    transaction.report?.purpose ? esc(transaction.report.purpose) : '',
    transaction.report?.trip ? `trip: ${esc(transaction.report.trip)}` : '',
    transaction.report?.paidWith ? esc(transaction.report.paidWith) : '',
  ]
    .filter(Boolean)
    .join(' · ')

  return `<li class="rep">
    ${thumb}
    <div class="repbody">
      <div class="rephead"><b>${esc(transaction.report?.item || transaction.description)}</b>
        <span class="num">${money2(transaction.amount)}</span></div>
      <div class="note">${shortDate(transaction.occurredOn)} · ${esc(transaction.description)} · ${esc(
        categoryName,
      )}</div>
      ${details ? `<div class="note">${details}</div>` : ''}
      ${
        transaction.amazon
          ? `<div class="amz">⚠️ Amazon purchase needs details: ${esc(
              transaction.amazon.missing.join(', '),
            )}</div>`
          : ''
      }
      <div class="acts">
        <span class="tag ${STATUS_CLASS[status] ?? 'st-need'}">${esc(status)}</span>
        ${
          canEdit
            ? `<button class="btn small" data-edit="entries:${transaction.id}">${
                transaction.report?.hasReceipt ? 'Edit' : 'Edit / add receipt'
              }</button>`
            : ''
        }
      </div>
    </div></li>`
}

/** Trip totals for the month, so a client visit can be seen as one number. */
function renderTrips(reported: TransactionView[]): void {
  const trips = new Map<string, string>()
  for (const transaction of reported) {
    const trip = transaction.report?.trip
    if (!trip) continue
    trips.set(trip, addMoney(trips.get(trip) ?? '0.00', transaction.amount))
  }

  const element = $('#trips')
  if (!element) return

  element.innerHTML = trips.size
    ? [...trips.entries()]
        .map(
          ([trip, total]) =>
            `<span class="tag">${esc(trip)} <b class="num">${money2(total)}</b></span>`,
        )
        .join('')
    : ''
}
